import { ChangeEvent, useCallback, useMemo } from 'react';
import {
  FormField,
  Select,
  SelectOption,
  SelectOptionsGroup,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNodeAction,
  type MasonryEngineActionHandler,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import { type MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import { replaceSearchParam } from 'utils/replace-search-param.util';

// SCFHP override for stock `selectInput`. Two reasons it exists:
//   1. Stock renders the Select controlled with `value=""`, which
//      collides with genesis-core's disabled placeholder option and
//      snaps back on every change — user picks never commit. We use
//      `defaultValue` (uncontrolled).
//   2. On change, fire a custom `setFilter` action; its handler calls
//      `messagingController.sendMessage(nodeId, {type:'reload',
//      setQueryParams})` directly — same pattern paginationLayout's
//      setPage uses. Going through the messagingController is what
//      makes the new query params actually reach the masonry-node
//      fetch URL; the engine-level `reload` action does NOT propagate
//      `setQueryParams` from its payload.

const RESOLVER_PARAM_BY_FIELD: Record<string, string> = {
  statusCode: 'statusCodes',
  formType: 'formTypes',
  submissionDate: 'submissionDateFrom',
};

const SENTINEL_VALUES_FOR_ALL = new Set(['', 'ALL']);
const RELOAD_NODE_ID = 'formStatuses$rootWebPaginationLayout';

export const FILTER_NAMESPACE = 'scfhp_form_status_filter';

type SetFilterAction = MasonryEngineNodeAction<
  'setFilter',
  {
    nodeId: string;
    paramKey: string;
    paramValue: string;
  }
>;

export const getSetFilterAction = (
  nodeId: string,
  paramKey: string,
  paramValue: string,
): SetFilterAction => ({
  type: 'setFilter',
  namespace: FILTER_NAMESPACE,
  payload: { nodeId, paramKey, paramValue },
});

// Mirrors paginationLayout's setPageActionHandler — calls
// messagingController.sendMessage so `setQueryParams` actually flows
// into the masonry-node fetch URL on reload.
export const setFilterActionHandler: MasonryEngineActionHandler<
  MasonryEngineNodeAction<'setFilter', Record<string, any>>,
  MasonryDriverAction
> = ({ payload }, { messagingController }) => {
  messagingController.sendMessage(payload.nodeId as string, {
    type: 'reload',
    payload: {
      renderIntermediately: 'loader',
      setQueryParams: (current: Record<string, string>) => {
        const next = { ...current };
        if (payload.paramValue === '') {
          delete next[payload.paramKey as string];
        } else {
          next[payload.paramKey as string] = payload.paramValue as string;
        }
        // Reset to page 1 so the user doesn't land on an out-of-range
        // page after the filtered result count shrinks.
        delete next.page;
        return next;
      },
    },
  });
};

type Option = {
  label: string;
  value?: string;
  disabled?: boolean;
  options?: { label: string; value: string; disabled?: boolean }[];
};

type FormFieldProperties = {
  formFieldId: string;
  label?: string;
  hideLabel?: boolean;
  hint?: string;
  showOptionalFlag?: boolean;
  inputStatus?: 'success' | 'warning' | 'error';
  statusMessage?: string;
};

type Props = {
  formFieldProperties: FormFieldProperties;
  value?: string;
  placeholder?: string;
  options: Option[];
  disabled?: boolean;
  locatorId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rootElementRef?: any;
};

export const ScfhpSelectInputRenderer = ({
  formFieldProperties,
  value,
  placeholder,
  options,
  disabled,
  locatorId,
  rootElementRef,
}: Props) => {
  const { formFieldId, label, hideLabel, hint, showOptionalFlag } =
    formFieldProperties;

  const invokeAction = useMasonryEngineActionEmitter();

  const selectOptions = useMemo(
    () =>
      options.map((opt, index) => {
        if (opt.options) {
          return {
            key: `${formFieldId}-${index}`,
            label: opt.label,
            disabled: opt.disabled,
            options: opt.options.map((child, childIdx) => ({
              key: `${formFieldId}-${index}${childIdx}`,
              value: child.value,
              disabled: child.disabled,
              label: child.label,
            })),
          };
        }
        return {
          key: `${formFieldId}-${index}`,
          label: opt.label,
          value: opt.value ?? '',
          disabled: opt.disabled,
        } as SelectOption;
      }),
    [formFieldId, options],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const raw = event.currentTarget.value;
      const paramKey = RESOLVER_PARAM_BY_FIELD[formFieldId];
      if (!paramKey) return;
      const paramValue = SENTINEL_VALUES_FOR_ALL.has(raw) ? '' : raw;
      // Sync URL bar immediately for shareability (paginationLayout's
      // renderer does the same). The setFilter handler then re-applies
      // via setQueryParams so the SDK's reload uses it for the fetch.
      replaceSearchParam(paramKey, paramValue);
      invokeAction(getSetFilterAction(RELOAD_NODE_ID, paramKey, paramValue));
    },
    [formFieldId, invokeAction],
  );

  return (
    <FormField
      ref={rootElementRef}
      id={formFieldId}
      label={label}
      name={formFieldId}
      hint={hint}
      hideLabel={hideLabel}
      required={!showOptionalFlag}
      data-testid={locatorId}
    >
      <Select
        name={formFieldId}
        placeholder={placeholder}
        defaultValue={value || undefined}
        disabled={disabled}
        options={selectOptions as SelectOptionsGroup[]}
        onChange={handleChange}
      />
    </FormField>
  );
};
